import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Check, Crown, Zap, Radio, Shield, Star, ArrowRight } from 'lucide-react';
import { Logo } from '../Logo';
import { subscriptionPlans, createCheckoutSession, redirectToCheckout, formatPrice } from '../../lib/stripe/subscriptions';
import { BottomSheet } from '../ui/BottomSheet';

interface SubscriptionModalProps {
  onClose: () => void;
  onSuccess: () => void;
  selectedPlan?: 'monthly' | 'yearly';
}

export function SubscriptionModal({ onClose, onSuccess, selectedPlan = 'yearly' }: SubscriptionModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState(selectedPlan);

  const handleSubscribe = async (planId: 'monthly' | 'yearly') => {
    try {
      setLoading(true);
      setError(null);

      const sessionId = await createCheckoutSession(planId);
      await redirectToCheckout(sessionId);
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(err.message || t('subscription.failedStart'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Zap, titleKey: 'subscription.liveStreamAccess', descKey: 'subscription.liveStreamDesc' },
    { icon: Sparkles, titleKey: 'subscription.createListings', descKey: 'subscription.createListingsDesc' },
    { icon: Crown, titleKey: 'subscription.buildCommunities', descKey: 'subscription.buildCommunitiesDesc' },
    { icon: Radio, titleKey: 'subscription.hostEvents', descKey: 'subscription.hostEventsDesc' },
    { icon: Star, titleKey: 'subscription.advancedFeatures', descKey: 'subscription.advancedFeaturesDesc' },
    { icon: Shield, titleKey: 'subscription.prioritySupport', descKey: 'subscription.prioritySupportDesc' }
  ];

  const activePlanData = subscriptionPlans.find(p => p.id === activePlan);

  return (
    <BottomSheet
      isOpen
      onClose={onClose}
      maxHeight={90}
      desktopMode="modal"
      desktopMaxWidthClass="max-w-4xl"
      panelClassName="bg-[#FAFCF7] rounded-t-3xl lg:rounded-2xl"
      closeOnBackdrop
      zIndexClassName="z-[9999]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
        className="flex flex-col"
      >
        {/* Header with decorative background */}
        <div className="relative overflow-hidden flex-shrink-0">
          {/* Decorative green blobs */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent-text/10 via-[#F3F7EE] to-accent-text/5" />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-text/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-accent-text/8 rounded-full blur-2xl" />

          {/* Close button - min 44px touch target */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-content/60 hover:text-content rounded-full hover:bg-white/60 transition-colors -mt-1 -mr-1"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative px-6 pt-8 pb-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Logo className="h-10 w-10 text-accent-text" />
              <span className="text-xl font-gelica font-bold text-[#304F3E]">Mindful Family</span>
            </div>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent-text/15 rounded-2xl mb-4">
              <Crown className="h-7 w-7 text-accent-text" />
            </div>
            <h2 id="subscription-modal-title" className="text-2xl sm:text-3xl font-gelica font-bold text-[#304F3E] mb-2">
              {t('subscription.upgradeTitle')}
            </h2>
            <p className="text-content/60 text-[15px] max-w-md mx-auto">
              {t('subscription.upgradeSubtitle')}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 sm:px-6 pb-8 flex-1 min-h-0">

          {/* Features - double column */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-white rounded-xl p-3.5 border border-gray-100/80"
                >
                  <div className="flex-shrink-0 w-9 h-9 bg-accent-text/10 rounded-lg flex items-center justify-center">
                    <Icon className="h-4.5 w-4.5 text-accent-text" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-content text-sm leading-tight">{t(feature.titleKey)}</h3>
                    <p className="text-content/50 text-xs mt-0.5 leading-snug">{t(feature.descKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Plan Toggle - touch-friendly buttons */}
          <div className="bg-white rounded-2xl border border-gray-100 p-1.5 flex mb-5">
            {subscriptionPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setActivePlan(plan.id)}
                type="button"
                className={`relative flex-1 py-4 px-4 min-h-[56px] rounded-xl text-center transition-all duration-200 ${
                  activePlan === plan.id
                    ? 'bg-accent-text text-white shadow-md'
                    : 'text-content/60 hover:text-content active:bg-gray-50'
                }`}
              >
                {plan.savings && (
                  <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    activePlan === plan.id
                      ? 'bg-white text-accent-text'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {plan.savings}
                  </span>
                )}
                <div className={`text-xs font-medium mb-0.5 ${activePlan === plan.id ? 'text-white/80' : ''}`}>
                  {plan.name}
                </div>
                <div className="flex items-baseline justify-center gap-0.5">
                  <span className={`text-xl font-bold ${activePlan === plan.id ? 'text-white' : 'text-content'}`}>
                    {formatPrice(plan.price, plan.currency)}
                  </span>
                  <span className={`text-xs ${activePlan === plan.id ? 'text-white/70' : 'text-content/40'}`}>
                    /{plan.interval}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Yearly savings note */}
          {activePlan === 'yearly' && (
            <div className="flex items-center justify-center gap-2 mb-5 py-2.5 px-4 bg-green-50 rounded-xl border border-green-100">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700 font-medium">{t('subscription.saveCompared')}</span>
            </div>
          )}

          {/* CTA Button - min 48px height for touch */}
          <button
            type="button"
            onClick={() => activePlanData && handleSubscribe(activePlanData.id)}
            disabled={loading}
            className="w-full min-h-[48px] py-4 px-6 bg-accent-text text-white font-semibold text-base rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-accent-text/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('subscription.processing')}
              </>
            ) : (
              <>
                {t('subscription.choosePlan', { plan: activePlanData?.name || '' })}
                <ArrowRight className="h-4.5 w-4.5" />
              </>
            )}
          </button>

          {/* Error */}
          {error && (
            <div className="mt-4 p-3.5 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Trust badges */}
          <div className="mt-5 flex flex-col items-center gap-2">
            <div className="flex items-center gap-4 text-content/30">
              <Shield className="h-4 w-4" />
              <span className="text-xs">•</span>
              <span className="text-xs font-medium">{t('subscription.securePowered')}</span>
            </div>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}
